import { useEffect, useState } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { StyledTextInput } from "common/core/form/text";
import { isAriaInvalid, FormattedFieldError, ErrorMessage } from "common/core/form/error";
import Button from "common/core/button";
import { useForm, type UseFormReturn } from "common/core/form";
import { Heading, Paragraph } from "common/core/typography";
import { MasterPrivacyPolicyUrl, UserTermsOfService } from "common/tos";
import { Footer, NextStepButton } from "common/signer/common";
import { useMutation } from "util/graphql";
import UpdateCustomerProfileMutation from "common/account/guest_signup/shared/update_customer_profile_mutation.graphql";
import { useMatchScreenClass } from "common/core/responsive";
import Icon from "common/core/icon";
import { PronounsButtonSubForm, type Pronouns } from "common/core/form/subforms/pronouns";
import { useId } from "util/html";
import { captureException } from "util/exception";

import type {
  InitiateSigningDocBundle_organizationTransaction as OrganizationTransaction,
  InitiateSigningDocBundle_participants as DocumentBundleParticipant,
} from "./index_docbundle_fragment.graphql";
import UpdateOrganizationTransactionCustomerMutation from "./update_organization_transaction_customer_mutation.graphql";
import Styles from "./customer_signer_names.module.scss";

export type FormValues = {
  email: string;
  firstName: string;
  lastName: string;
  middleName: string;
  pronouns?: string;
  customPronouns?: string;
};

const MESSAGES = defineMessages({
  firstNameField: {
    id: "db48abc9-6542-40fa-af2e-a92dba5af2bd",
    defaultMessage: "First name",
  },
  lastNameField: {
    id: "2fb3c029-01f7-4ddb-bebf-1daddb25ede1",
    defaultMessage: "Last name",
  },
  middleNameField: {
    id: "aa9fd40d-e1f0-45fa-909a-efe16f26d0b5",
    defaultMessage: "Middle name",
  },
  genericError: {
    id: "cd125cdc-2a20-4b05-8136-727d475f2067",
    defaultMessage: "An error occurred. Please try again.",
  },
  duplicateError: {
    id: "005fc94b-97cc-422a-9cb0-ffc80c0227c0",
    defaultMessage:
      "Cannot add a signer with the same name and email as another signer. Please enter a different name or email.",
  },
});

const REQUIRED_MESSAGES = defineMessages({
  firstNameField: {
    id: "d5e49cbb-c675-4ba5-af2a-d3bb673aa635",
    defaultMessage: "First name is required",
  },
  lastNameField: {
    id: "c09344e6-f0a4-4266-b60b-8098cc3174bf",
    defaultMessage: "Last name is required",
  },
});

type Props = {
  organizationTransaction: OrganizationTransaction;
  selectedUserIds: string[];
  bundleParticipants: DocumentBundleParticipant[];
  onComplete: () => void;
};

export function CustomerSignerNames({
  organizationTransaction,
  bundleParticipants,
  selectedUserIds,
  onComplete,
}: Props) {
  const errorId = useId();
  // compute and cache customerSigners with missing names so we don't lose the initial list after updating their names
  const [selectedUsersMissingCustomerName] = useState(() =>
    selectedUserIds.filter((userId) => {
      const customerSigner = organizationTransaction.customerSigners.find(
        (signer) => signer.userId === userId && (!signer.firstName || !signer.lastName),
      );
      return Boolean(customerSigner);
    }),
  );
  const [currentCosignerIndex, setCurrentCosignerIndex] = useState(0);
  // the form gets the default values from SignerIdentity, but we don't create new signer identities
  // until after we update all consigners, which leads to stale data when going backwards in the form.
  // therefore we need a piece of state to hold on to the updates
  const [inProgressValues, setInProgressValues] = useState<FormValues[]>([]);
  const [submissionError, setSubmissionError] = useState<string>("");
  const intl = useIntl();
  const currentCosignerUserId = selectedUsersMissingCustomerName[currentCosignerIndex];
  const currentCosignerUser = bundleParticipants.find(
    (participant) => participant.userId === currentCosignerUserId,
  );

  const updateCustomerProfileMutateFn = useMutation(UpdateCustomerProfileMutation);
  const updateOrganizationTransactionCustomerMutateFn = useMutation(
    UpdateOrganizationTransactionCustomerMutation,
  );

  const cosignerTransactionCustomerSigner = organizationTransaction.customerSigners.find(
    (c) => c.userId === currentCosignerUser?.userId,
  );
  const isCurrentUser = cosignerTransactionCustomerSigner?.isCurrentUser;
  const isSmall = useMatchScreenClass("xs", "sm");

  const defaultValues = inProgressValues[currentCosignerIndex] || {
    email: currentCosignerUser?.email || "",
    firstName: currentCosignerUser?.firstName || "",
    lastName: currentCosignerUser?.lastName || "",
    middleName: currentCosignerUser?.middleName || "",
    pronouns: "",
  };

  const form = useForm<FormValues>({
    defaultValues,
  });

  const onSuccess = (formValues: FormValues) => {
    setInProgressValues([
      ...inProgressValues.slice(0, currentCosignerIndex),
      formValues,
      ...inProgressValues.slice(currentCosignerIndex + 1),
    ]);
    setCurrentCosignerIndex(currentCosignerIndex + 1);
  };

  const onBack = () => {
    setCurrentCosignerIndex(currentCosignerIndex - 1 || 0);
  };

  useEffect(() => {
    form.reset(defaultValues);
  }, [currentCosignerIndex]);
  const { errors, isSubmitting } = form.formState;
  const watchPronouns = form.watch("pronouns") as Pronouns;

  async function onSubmit(formValues: FormValues, userId?: string) {
    if (!userId) {
      throw new Error("Cosigner user not found.");
    }

    const { firstName, middleName, lastName, email, pronouns, customPronouns } = formValues;
    const enteredPronouns = pronouns === "Other" ? customPronouns : pronouns;

    await updateCustomerProfileMutateFn({
      variables: {
        mutationInput: {
          id: userId,
          acceptedTos: true,
          firstName,
          middleName,
          lastName,
          pronouns: enteredPronouns,
        },
      },
    });

    if (cosignerTransactionCustomerSigner) {
      await updateOrganizationTransactionCustomerMutateFn({
        variables: {
          input: {
            organizationTransactionId: organizationTransaction.id,
            customer: {
              id: cosignerTransactionCustomerSigner.id,
              firstName,
              middleName,
              lastName,
              email,
            },
          },
        },
      });
    } else {
      throw new Error("Cosigner not found in transaction customers.");
    }

    if (currentCosignerIndex + 1 >= selectedUsersMissingCustomerName.length) {
      onComplete();
      return;
    }
    onSuccess(formValues);
  }

  return (
    <div className={Styles.container}>
      <form
        onSubmit={form.handleSubmit(async (formValues) => {
          try {
            await onSubmit(formValues, currentCosignerUserId);
          } catch (error) {
            captureException(error);
            const hasDuplicate = organizationTransaction.customerSigners.find((signer) => {
              const signerFirstName = signer.firstName || "";
              const signerMiddleName = signer.middleName || "";
              const signerLastName = signer.lastName || "";
              const signerEmail = signer.email || "";
              return (
                signer.userId !== currentCosignerUserId &&
                signerFirstName.toLowerCase() === formValues.firstName.toLowerCase() &&
                signerMiddleName.toLowerCase() === formValues.middleName.toLowerCase() &&
                signerLastName.toLowerCase() === formValues.lastName.toLowerCase() &&
                signerEmail.toLowerCase() === currentCosignerUser?.email?.toLowerCase()
              );
            });
            if (hasDuplicate) {
              setSubmissionError(intl.formatMessage(MESSAGES.duplicateError));
            } else {
              setSubmissionError(intl.formatMessage(MESSAGES.genericError));
            }
          }
        })}
      >
        <fieldset>
          <legend>
            <Heading textStyle={isSmall ? "subtitle" : "headingFour"} level={"h1"}>
              {isCurrentUser ? (
                <FormattedMessage
                  id="a729e776-68e8-4821-8340-59613d252793"
                  defaultMessage="Enter your full name as it appears on your ID"
                />
              ) : (
                <FormattedMessage
                  id="2e94bc2c-6623-421a-b030-cd06ee0105ba"
                  defaultMessage="Enter {email}'s full name as it appears on their ID"
                  values={{ email: currentCosignerUser?.email }}
                />
              )}
            </Heading>
          </legend>
          <div className={Styles.fields}>
            <div>
              <StyledTextInput
                label={intl.formatMessage(MESSAGES.firstNameField)}
                aria-invalid={isAriaInvalid(errors.firstName)}
                displayRequiredAsterisk
                autoComplete="given-name"
                {...form.register("firstName", {
                  onChange: () => {
                    setSubmissionError("");
                  },
                  required: intl.formatMessage(REQUIRED_MESSAGES.firstNameField),
                })}
                data-automation-id="first-name-field"
              />
              {errors.firstName && (
                <FormattedFieldError inputName="firstName" error={errors.firstName} />
              )}
            </div>
            <div>
              <StyledTextInput
                label={intl.formatMessage(MESSAGES.middleNameField)}
                aria-invalid={isAriaInvalid(errors.middleName)}
                autoComplete="additional-name"
                {...form.register("middleName", {
                  onChange: () => {
                    setSubmissionError("");
                  },
                })}
                data-automation-id="middle-name-field"
              />
            </div>
            <div>
              <StyledTextInput
                label={intl.formatMessage(MESSAGES.lastNameField)}
                aria-invalid={isAriaInvalid(errors.lastName)}
                displayRequiredAsterisk
                autoComplete="family-name"
                {...form.register("lastName", {
                  onChange: () => {
                    setSubmissionError("");
                  },
                  required: intl.formatMessage(REQUIRED_MESSAGES.lastNameField),
                })}
                data-automation-id="last-name-field"
              />
              {errors.lastName && (
                <FormattedFieldError inputName="lastName" error={errors.lastName} />
              )}
            </div>
          </div>
          <div className={Styles.pronouns}>
            <PronounsButtonSubForm
              className={Styles.pronounsButton}
              selectedPronouns={watchPronouns}
              pronounsProps={form.register("pronouns")}
              customPronounsProps={form.register("customPronouns")}
              resetField={
                form.resetField as UseFormReturn<{
                  pronouns?: Pronouns;
                  customPronouns?: string;
                }>["resetField"]
              }
              showTooltip={false}
            />
            {submissionError && <ErrorMessage id={errorId} message={submissionError} />}
          </div>
        </fieldset>

        <Paragraph size="small">
          <FormattedMessage
            id="f5ed7956-7ca7-4dfb-b445-0cfa4e847b27"
            defaultMessage="By clicking Continue, you are agreeing to the {termsOfUseLink}. For information on our privacy and data use practices, see our {privacyPolicyLink}."
            values={{
              termsOfUseLink: <UserTermsOfService underlined />,
              privacyPolicyLink: <MasterPrivacyPolicyUrl underlined />,
            }}
          />
        </Paragraph>
        <Footer
          stackButtons={isSmall}
          previousStepButton={
            currentCosignerIndex > 0 ? (
              <Button
                disabled={isSubmitting}
                buttonSize={isSmall ? undefined : "large"}
                variant="secondary"
                buttonColor="action"
                onClick={onBack}
              >
                <Icon className={Styles.icon} name="arrow-left" />
                <FormattedMessage id="f5ed7956-7ca7-4dfb-b445-0cfa4e847b27" defaultMessage="Back" />
              </Button>
            ) : undefined
          }
          nextStepButton={
            <NextStepButton
              type="submit"
              buttonSize={isSmall ? undefined : "large"}
              automationId="confirm-signer"
              fullwidth={isSmall}
              isLoading={isSubmitting}
              aria-describedby={submissionError ? errorId : undefined}
              disabled={
                isSubmitting || submissionError === intl.formatMessage(MESSAGES.duplicateError)
              }
            />
          }
        />
      </form>
    </div>
  );
}
